<template>
<div class="fields-container">
  <CustomField :label="deliveryTimeInputLabel"
               v-slot="{ id }"
               class="order-delivery__time"
               :class="{ 'order-delivery__time--asap': deliveryTime === null }"
               v-show="deliveryTimeEnabled"
  >
    <input ref="flatpickrInput" :id="id" class="form-control order-delivery__flatpickr-input"/>
    <div v-if="deliveryTime === null" class="order-delivery__time-asap-text">
      <span>Как можно скорее</span>
    </div>
    <button v-if="deliveryTime !== null"
            @click="$emit('update:delivery-time', null)"
            title="Как можно скорее"
            tabindex="-1"
            class="order-delivery__time-asap-button"
    >
      <i class="fa fa-times"></i>
    </button>
  </CustomField>
  <CustomInput class="order-delivery-num-people"
               label="Персон"
               v-model="num_peopleAccessor"
               min="1"
               type="number"
               inputmode="numeric"
               placeholder="Число"
               v-show="personsCountEnabled"
  />
  <div class="order-delivery__expand-button-wrapper" v-show="orderOptions.length > 0">
    <!-- Just so it's aligned well with fields (they can also have error text), -->
    <label class="form-control-label invisible">.</label>
    <button @click="collapseNotes = !collapseNotes"
            :aria-expanded="(!collapseNotes).toString()"
            class="order-delivery__expand-button order-delivery__expand-notes-button"
            type="button"
    >
      <span class="order-delivery__expand-button-text">Комментарий</span>
      <IconArrowDown />
    </button>
  </div>
  <TransitionExpand>
    <CustomField v-if="!collapseNotes" label="Комментарий" class="order-delivery-notes">
      <textarea v-model="notesAccessor" class="form-control" placeholder="Комментарий к заказу" />
    </CustomField>
  </TransitionExpand>
</div>
</template>

<script>
import flatpickr from 'flatpickr';
import { Russian } from 'flatpickr/dist/l10n/ru';
import TransitionExpand from '@/components/AsyncTransitionExpand';
import CustomField from '@/components/inputs/CustomField';
import CustomInput from '@/components/inputs/CustomInput';
import { mapPropsToAccessors } from '@/helpers/mapPropsToAccessors';
import { mapGetters } from 'vuex';

export default {
  components: {
    CustomField,
    CustomInput,
    TransitionExpand,
    IconArrowDown: () => import(/* webpackPreload: true */ '@/assets/images/icons/icon-arrow-down-small.svg?inline'),
  },

  props: {
    num_people: {},
    notes: {},
    deliveryTimeInputLabel: {
      type: String,
    },
    deliveryTime: {
      type: [String, null], // `null` for 'ASAP'
    },
  },

  created() {
    this.collapseNotes = this.orderOptions.length > 0 && this.deliverySettings.notes_field_state === 'collapsed'
  },

  mounted() {
    const minDate = new Date();
    let draftDate = undefined;
    this.deliveryTimeFlatpickr = flatpickr(this.$refs.flatpickrInput, {
      enableTime: true,
      locale: Russian,
      altInput: true,
      altFormat: "d.m.Y – H:i",
      minuteIncrement: 10,
      time_24hr: true,
      minDate,
      defaultHour: new Date().getHours(),
      defaultMinute: 0,
      dateFormat: "Y-m-d H:i", // As our API requires.
      // TODO `static: true, inline: true` so it is scrolled along with the modal?
      onChange: (dates, dateStr, instance) => {
        draftDate = dates[0];
      },
      onClose: (_, dateStr, instance) => {
        let newVal = dateStr ? dateStr : null;
        function isReasonablyLater(date) {
          const msInMin = 60 * 1000;
          return (
            date.valueOf() / msInMin > (Date.now() + 4 * msInMin) / msInMin
          );
        }
        if (draftDate !== undefined && !isReasonablyLater(draftDate)) {
          newVal = null;
          instance.setDate(null);
        }
        if (newVal !== this.deliveryTime) {
          this.$emit("update:delivery-time", newVal);
        }
      }
    });

    this.$watch("deliveryTime", n => this.deliveryTimeFlatpickr.setDate(n), {
      immediate: true
    });
  },

  beforeDestroy() {
    if (this.datetimeFieldInitialized) {
      this.destroyDeliveryTimeInput();
    }
  },

  computed: {
    ...mapPropsToAccessors(['num_people', 'notes']),

    ...mapGetters({
      orderOptions: 'store/orderOptions',
      deliverySettings: 'settings/delivery',
    }),

    datetimeFieldInitialized() {
      return (
        this.deliveryTimeFlatpickr !== null &&
        this.stopUpdatingFlatpickr !== null
      );
    },

    deliveryTimeEnabled() {
      return this.orderOptions.indexOf('delivery_time') !== -1
    },

    personsCountEnabled() {
      return this.orderOptions.indexOf('persons_count') !== -1
    },

    showDeliveryTimeField() {
      return true;
    }
  },

  methods: {
    initDatetimeInput() {
      const minDate = new Date();
      let draftDate = undefined;
      this.deliveryTimeFlatpickr = flatpickr(this.$refs.flatpickrInput, {
        enableTime: true,
        locale: Russian,
        altInput: true,
        altFormat: "d.m.Y – H:i",
        minuteIncrement: 10,
        time_24hr: true,
        minDate,
        defaultHour: new Date().getHours(),
        defaultMinute: 0,
        dateFormat: "Y-m-d H:i", // As our API requires.
        // TODO `static: true, inline: true` so it is scrolled along with the modal?
        onChange: (dates, dateStr, instance) => {
          draftDate = dates[0];
        },
        onClose: (_, dateStr, instance) => {
          let newVal = dateStr ? dateStr : null;
          function isReasonablyLater(date) {
            const msInMin = 60 * 1000;
            return (
              date.valueOf() / msInMin > (Date.now() + 4 * msInMin) / msInMin
            );
          }
          if (draftDate !== undefined && !isReasonablyLater(draftDate)) {
            newVal = null;
            instance.setDate(null);
          }
          if (newVal !== this.deliveryTime) {
            this.$emit("update:delivery-time", newVal);
          }
        }
      });
      this.stopUpdatingFlatpickr = this.$watch(
        "deliveryTime",
        n => {
          this.deliveryTimeFlatpickr.setDate(n);
        },
        { immediate: true }
      );
    },

    destroyDeliveryTimeInput() {
      this.stopUpdatingFlatpickr();
      this.stopUpdatingFlatpickr = null;
      this.deliveryTimeFlatpickr.destroy();
      this.deliveryTimeFlatpickr = null;
    },

    scrollElToTop(el) {
      // setTimeout so it doesn't get interrupted after a brief moment in Chrome for Android.
      // Also see the comment below.
      const initialTop = el.getBoundingClientRect().top;
      setTimeout(() => {
        if (initialTop !== el.getBoundingClientRect().top) {
          // If scrolling is being handled for us.
          // To avoid the bug in Safari on iOS, where the modal is gets scrolled far past the input.
          return;
        }
        el.scrollIntoView({ block: "start", behavior: "smooth" });
      }, 150);
    }
  },

  watch: {
    showDeliveryTimeField: {
      immediate: true,
      handler(n) {
        if (n === false && !this.datetimeFieldInitialized) {
          this.$nextTick(() => {
            this.initDatetimeInput();
          });
        } else if (n === true && this.datetimeFieldInitialized) {
          this.destroyDeliveryTimeInput();
        }
      }
    },

    orderOptions: {
      immediate: true,
      handler(value) {
        if (!value.length) {
          this.collapseNotes = false
        }
      }
    }
  },

  data() {
    return {
      deliveryTimeFlatpickr: null,
      stopUpdatingFlatpickr: null,
      collapseNotes: true,
    };
  },
}
</script>
